import { FC, useEffect } from 'react'
import {
    RadioButtonWrapper,
    StyledBadge,
    StyledLabel,
    StyledRadioTileGroupItem,
} from './RadioTileGroupItem.styles'
import { RadioTileGroupItemProps } from './RadioTileGroupItem.types'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import { Image } from 'components/shared/Image'
import RadioInput from 'components/shared/Radio/input'
import { useResolutions } from 'store/resolutions'
import { FormattedMessage } from 'react-intl'
import { formatPrice } from 'helpers/helpers'
import { scrollTo } from 'helpers/animateScroll'

export const RadioTileGroupItem: FC<RadioTileGroupItemProps> = ({
    id,
    value,
    label,
    children,
    name,
    description,
    price,
    time,
    badgeText,
    image,
    isChecked = false,
    isDisabled = false,
    isError = false,
    isExpandable,
    isAutoScrolling,
    onChange,
    onCheckDetailsClick,
    'data-testid': testId,
}: RadioTileGroupItemProps) => {
    const { isMobile } = useResolutions()

    const handleClick = () => {
        if (!isDisabled && !isChecked) {
            onChange?.(value)
        }
    }

    useEffect(() => {
        if (!isAutoScrolling || !isChecked) return

        // after selecting item align it's box with the top of the vieport
        scrollTo({
            id: `item-${id}`,
            duration: 500,
            isMobile,
        })
    }, [isAutoScrolling, isChecked])

    return (
        <StyledRadioTileGroupItem
            gap={12}
            justify="space-between"
            align="flex-start"
            width="100%"
            isChecked={isChecked}
            isDisabled={isDisabled}
            isError={isError}
            onClick={handleClick}
            id={`item-${id}`}
            data-testid={testId ? `${testId}-select` : undefined}
        >
            <Box direction="column" gap={[24, 40]} width="100%">
                <Box direction="row" gap={[12, 16]} align="center" width="100%">
                    <RadioButtonWrapper>
                        <RadioInput
                            id={id}
                            option={name || label}
                            value={value}
                            isChecked={isChecked}
                            isDisabled={isDisabled}
                            ariaLabelledBy={`${id}-label`}
                        />
                    </RadioButtonWrapper>
                    {!isMobile && (
                        <Image
                            src={image}
                            alt={name}
                            width={96}
                            height={96}
                            borderRadius={6}
                        />
                    )}
                    <Box direction="column" gap={[8, 16]} isFullWidth>
                        <Box direction="column" gap={[0, 4]}>
                            <div>
                                {!!badgeText && (
                                    <StyledBadge>{badgeText}</StyledBadge>
                                )}
                                <StyledLabel
                                    variant="body"
                                    id={`${id}-label`}
                                    color="grayscale_900"
                                >
                                    {label}
                                </StyledLabel>
                            </div>
                            {!!description && !isMobile && (
                                <>
                                    <Text
                                        variant="bodySmall"
                                        color="grayscale_800"
                                        truncateAfter={2}
                                    >
                                        {description}
                                    </Text>
                                    <Text
                                        variant="bodySmall"
                                        color="secondary_500"
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            onCheckDetailsClick &&
                                                onCheckDetailsClick(id)
                                        }}
                                        data-testid={
                                            testId
                                                ? `${testId}-button-view-details`
                                                : undefined
                                        }
                                    >
                                        <FormattedMessage
                                            id="upsells.wellness.more_details"
                                            defaultMessage="Zobacz szczegóły"
                                        />
                                    </Text>
                                </>
                            )}
                        </Box>
                    </Box>
                    <Box direction="column" align="flex-end">
                        {!!price?.length && (
                            <Box direction="row" gap={4} align="baseline">
                                {price.length > 1 && (
                                    <Text
                                        variant="bodySmall"
                                        color="secondary_500"
                                        isCapitalized
                                    >
                                        <FormattedMessage
                                            id="global.price.label_from"
                                            defaultMessage="od"
                                        />
                                    </Text>
                                )}
                                <Text variant="heading4" color="secondary_500">
                                    {formatPrice(price[0])}
                                </Text>
                                <Text
                                    variant="bodyExtraSmallStrong"
                                    color="secondary_500"
                                >
                                    PLN
                                </Text>
                            </Box>
                        )}
                        {!!time?.length && (
                            <Text
                                variant="bodySmall"
                                color="grayscale_800"
                                hasNoWrap
                            >
                                <FormattedMessage
                                    id="global.units.minutes_abbreviation"
                                    defaultMessage="{number} min"
                                    values={{
                                        number: time.join('/'),
                                    }}
                                />
                            </Text>
                        )}
                    </Box>
                </Box>
                {!!description && isMobile && isChecked && (
                    <Box direction="column">
                        <Text
                            variant="bodySmall"
                            color="grayscale_800"
                            truncateAfter={2}
                        >
                            {description}
                        </Text>
                        <Text
                            variant="bodySmall"
                            color="secondary_500"
                            onClick={() =>
                                onCheckDetailsClick && onCheckDetailsClick(id)
                            }
                        >
                            <FormattedMessage
                                id="upsells.wellness.check_details_and_photos"
                                defaultMessage="Zobacz szczegóły i zdjęcia"
                            />
                        </Text>
                    </Box>
                )}
                {(!isExpandable || isChecked) && children}
            </Box>
        </StyledRadioTileGroupItem>
    )
}
